import { useCallback, useContext, useEffect, useState } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import type { AxiosError } from 'axios';

import { useBooleanState } from '@vyce/core/src/hooks';

import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { ChangeTeamManagersData, GetCompanyTeamsMembersDataRequest } from '@vyce/core/src/api/types';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { TeamMember } from '@vyce/core/src/types';
import { useTabFocus } from '@vyce/core/src/hooks/useTabFocus';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';

import { Props } from '../types';
import { getUrlItems } from '../../../../utils';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import { TeamModuleContext } from '../../../../contexts';

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'desc' }];

export const useWidgetData = ({ selectedCompanyId, signalForRerender, teamIds }: Props) => {
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const { isTabFocused } = useTabFocus();
  const { handleServerError } = useContext(NotificationContext);
  const { isMobile } = useContext(DeviceContext);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const { team } = useParams<{ team: string }>();
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);

  const {
    teamData,
    getTeamMembersRequest,
    deleteTeamMembersRequest,
    changeTeamManagersRequest,
    removeUsersFromChat,
  } = useContext(TeamModuleContext);

  const { sortModel, total, substring, setTotal, handleSortModelChange, handleSearchChange } = useTable({
    defaultSortModel,
  });

  const {
    isLoadingList,
    data,
    setNewData,
    setInitialLoadingStatusesFalse,
    setInitialLoadingStatusesTrue,
    resetLoadingList,
  } = useInfiniteScroll<TeamMember>([]);

  const teamDataId = teamData?.uuid ?? '';

  const getTeamMembers = async (offset: number = 0, signal?: AbortSignal) => {
    if (!selectedCompanyId || !isTabFocused) {
      return;
    }
    const itemStatusMap: Record<number, boolean> = setInitialLoadingStatusesFalse(offset, GRID_PAGE_SIZE);

    setLoadingTrue();
    const { id: teamId } = getUrlItems(team);
    try {
      const requestData: GetCompanyTeamsMembersDataRequest = {
        offset: offset,
        limit: GRID_PAGE_SIZE,
        substring,
        order_by: formatSortModel<TeamMember>(sortModel),
        team_ids: teamIds,
      };

      const {
        data: { items, count },
      } = await getTeamMembersRequest(selectedCompanyId, teamId, requestData, signal);
      setNewData({ newData: items, offset });
      setTotal(count);
      setInitialLoadingStatusesTrue({ offset, pageSize: GRID_PAGE_SIZE, itemStatusMap });
      setLoadingFalse();
    } catch (e) {
      const err = e as AxiosError;
      if (err.name !== 'CanceledError') {
        handleServerError(err);
        setLoadingFalse();
      }
    }
  };

  const deleteTeamMembers = useCallback(
    async (member: string) => {
      if (!selectedCompanyId || !teamDataId) {
        return;
      }
      try {
        setLoadingTrue();
        const requestData = { members: [member] };
        await deleteTeamMembersRequest(selectedCompanyId, teamDataId, requestData);
        const newTeamMembers = teamMembers.filter(item => item.user_id !== member);
        setTeamMembers(newTeamMembers);
        getTeamMembers(0);
        removeUsersFromChat?.({
          companyId: selectedCompanyId,
          entityId: teamDataId,
          userIds: [member],
        });
        setLoadingFalse();
      } catch (e) {
        handleServerError(e);
        setLoadingFalse();
      }
    },
    [teamMembers, selectedCompanyId, teamDataId]
  );

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const changeManager = useCallback(
    async (id: string, isRemove: boolean) => {
      if (!selectedCompanyId || !teamDataId) {
        return;
      }
      try {
        const requestData: ChangeTeamManagersData = {
          to_add: [],
          to_remove: [],
        };
        if (isRemove) {
          requestData?.to_remove?.push(id);
        } else {
          requestData?.to_add?.push(id);
        }
        await changeTeamManagersRequest(selectedCompanyId, teamDataId, requestData);
        resetCloseStatus();
        await getTeamMembers();
      } catch (e) {
        handleServerError(e);
      }
    },
    [selectedCompanyId, teamDataId]
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    getTeamMembers(0, signal);

    return () => {
      controller.abort();
    };
  }, [selectedCompanyId, sortModel, substring, signalForRerender]);

  return {
    workers: data,
    total,
    loading,
    sortModel,
    isMobile,
    isLoadingList,
    resetLoadingList,
    handleSortModelChange,
    handleSearchChange,
    deleteTeamMembers,
    getTeamMembers,
    changeManager,
    closeGridAction,
  };
};
